import {
  type RemoteConfig,
  fetchAndActivate,
  getBoolean,
  getNumber,
  getRemoteConfig,
  getString,
} from "firebase/remote-config";
import {
  REMOTE_CONFIG_DEFAULTS_BY_APP,
  type WebRemoteConfigKey,
} from "@graftus/remote-config-params";
import { app } from "@/config/firebaseConfig";

let defaultsApplied = false;
let initPromise: Promise<void> | null = null;

// Playwright visual-regression run (GRA-146): there is no Remote Config
// emulator, and the run has no reachable hosted backend to fetch from.
// Overrides layer on top of the real web defaults so the intelligence
// product (gated by `show_intelligence`) renders without a network call.
const TEST_MODE_REMOTE_CONFIG_OVERRIDES: Partial<Record<WebRemoteConfigKey, string>> = {
  show_intelligence: "1",
};

function getRc(): RemoteConfig {
  const rc = getRemoteConfig(app);
  if (!defaultsApplied) {
    rc.defaultConfig = {
      ...REMOTE_CONFIG_DEFAULTS_BY_APP.web,
      ...(import.meta.env.MODE === "test" ? TEST_MODE_REMOTE_CONFIG_OVERRIDES : {}),
    };
    defaultsApplied = true;
  }
  return rc;
}

/**
 * Sets default values and fetches/activates remote config.
 * Safe to call multiple times; only one fetch runs.
 * Non-blocking for callers; failures are logged.
 */
export async function initializeRemoteConfig(): Promise<void> {
  if (!initPromise) {
    initPromise = (async () => {
      try {
        const rc = getRc();

        // No Remote Config emulator exists; skip the network fetch in test
        // mode and rely on defaults (see the override above).
        if (import.meta.env.MODE === "test") return;

        if (import.meta.env.DEV) {
          rc.settings.minimumFetchIntervalMillis = 60_000;
        }

        const fetchedRemotely = await fetchAndActivate(rc);

        if (import.meta.env.DEV && fetchedRemotely) {
          console.log("[Remote Config] Fetched and activated from backend.");
        }
      } catch (error) {
        console.warn("[Remote Config] Initialize failed:", error);
      }
    })();
  }
  await initPromise;
}

export function getRemoteConfigString(key: WebRemoteConfigKey): string {
  return getString(getRc(), key);
}

export function getRemoteConfigBoolean(key: WebRemoteConfigKey): boolean {
  return getBoolean(getRc(), key);
}

export function getRemoteConfigNumber(key: WebRemoteConfigKey): number {
  return getNumber(getRc(), key);
}
